import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

type ToolKind = 'floorplan' | 'staging';

type Row = {
  tool: ToolKind;
  version_type: string;
  event_type: 'first' | 'refinement';
  credit_cost: number;
  updated_at: string | null;
};

const CONFIG: Record<ToolKind, {
  title: string;
  keys: Array<{ version_type: string; event_type: 'first' | 'refinement'; label: string }>;
}> = {
  floorplan: {
    title: 'Credit-Preise — Grundriss',
    keys: [
      { version_type: 'Floorplan2D', event_type: 'first',      label: '2D — Erste' },
      { version_type: 'Floorplan2D', event_type: 'refinement', label: '2D — Iteration' },
      { version_type: 'Floorplan3D', event_type: 'first',      label: '3D — Erste' },
      { version_type: 'Floorplan3D', event_type: 'refinement', label: '3D — Iteration' },
    ],
  },
  staging: {
    title: 'Credit-Preise — Staging',
    keys: [
      { version_type: 'Empty',  event_type: 'first',      label: 'Leerräumen — Erste' },
      { version_type: 'Empty',  event_type: 'refinement', label: 'Leerräumen — Iteration' },
      { version_type: 'Staged', event_type: 'first',      label: 'Einrichten — Erste' },
      { version_type: 'Staged', event_type: 'refinement', label: 'Einrichten — Iteration' },
    ],
  },
};

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.round(hours / 24);
  return `vor ${days} T`;
}

export const AdminCreditPricingView: React.FC<{ tool?: 'floorplan' | 'staging' }> = ({ tool = 'floorplan' }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keyOf = (vt: string, et: string) => `${vt}/${et}`;

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('credit_pricing')
      .select('tool, version_type, event_type, credit_cost, effective_from')
      .eq('tool', tool)
      .order('effective_from', { ascending: false });

    if (error) {
      setError(error.message);
      return;
    }
    const latest: Record<string, Row> = {};
    for (const r of data ?? []) {
      const k = keyOf(r.version_type, r.event_type);
      if (!latest[k]) {
        latest[k] = {
          tool,
          version_type: r.version_type as Row['version_type'],
          event_type: r.event_type as Row['event_type'],
          credit_cost: Number(r.credit_cost),
          updated_at: r.effective_from,
        };
      }
    }
    setRows(latest);
    setEdits({});
    setError(null);
  }, [tool]);

  useEffect(() => { void load(); }, [load]);

  const handleSave = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    const inserts: Array<{
      tool: ToolKind;
      version_type: string;
      event_type: string;
      credit_cost: number;
      updated_by: string;
    }> = [];

    for (const key of Object.keys(edits)) {
      const [vt, et] = key.split('/');
      const current = rows[key]?.credit_cost;
      const next = Number(edits[key]);
      if (!Number.isFinite(next) || next < 0) continue;
      if (next === current) continue;
      inserts.push({
        tool,
        version_type: vt,
        event_type: et,
        credit_cost: next,
        updated_by: user.id,
      });
    }

    if (inserts.length === 0) {
      setSaving(false);
      return;
    }

    const { error: insertError } = await supabase.from('credit_pricing').insert(inserts);
    if (insertError) {
      setError(insertError.message);
      setSaving(false);
      return;
    }
    await load();
    setSaving(false);
  }, [edits, rows, user, load, tool]);

  const handleReset = () => setEdits({});

  const anyDirty = Object.keys(edits).some(k => {
    const next = Number(edits[k]);
    const current = rows[k]?.credit_cost;
    return Number.isFinite(next) && next >= 0 && next !== current;
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{CONFIG[tool].title}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Änderungen werden als neue Zeile mit aktuellem Zeitstempel gespeichert. Die Historie bleibt erhalten.
        </p>
      </div>

      {error && (
        <div className="text-sm text-destructive">Fehler: {error}</div>
      )}

      <table className="w-full text-sm border rounded">
        <thead className="bg-muted/30">
          <tr>
            <th className="text-left p-2">Version / Event</th>
            <th className="text-left p-2 w-32">Credits</th>
            <th className="text-left p-2 w-32">Geändert</th>
          </tr>
        </thead>
        <tbody>
          {CONFIG[tool].keys.map(k => {
            const key = keyOf(k.version_type, k.event_type);
            const row = rows[key];
            const editValue = edits[key] ?? (row ? String(row.credit_cost) : '');
            return (
              <tr key={key} className="border-t">
                <td className="p-2">{k.label}</td>
                <td className="p-2">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={editValue}
                    onChange={e => setEdits(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-24 border rounded px-2 py-1"
                  />
                </td>
                <td className="p-2 text-muted-foreground">{relativeTime(row?.updated_at ?? null)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!anyDirty || saving}
          className="px-4 py-2 rounded bg-primary text-primary-foreground disabled:opacity-50"
        >
          {saving ? 'Speichere…' : 'Änderungen speichern'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!anyDirty || saving}
          className="px-4 py-2 rounded border disabled:opacity-50"
        >
          Rückgängig
        </button>
      </div>
    </div>
  );
};
